/*
 * Lemonade Training Oy, copy rights, all right reserved.
 * Live Box document subscription
 */

import { useEffect, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { firebaseDB, COLLECTIONS } from "@lemonadetraining/web-shared";
import type { Box } from "@lemonadetraining/contracts";

export interface UseBoxResult {
  box: (Box & { id: string }) | null;
  isLoading: boolean;
  /** True once the listener's error callback fired; the listener is then dead. */
  error: boolean;
}

/**
 * Subscribe to one Boxes/{boxID} document.
 *
 * Live rather than one-shot because callers gate optimistic role writes on
 * `grantVersion`, which another admin can move at any moment; a stale read
 * would send an expectedGrantVersion the server has already left behind.
 *
 * @param boxID - box to watch, or null to watch nothing
 * @param retryToken - bump to tear down and re-subscribe after an error
 */
export const useBox = (boxID: string | null, retryToken = 0): UseBoxResult => {
  const [state, setState] = useState<{
    boxID: string | null;
    box: (Box & { id: string }) | null;
    error: boolean;
  }>({ boxID: null, box: null, error: false });

  useEffect(() => {
    if (!boxID) return undefined;

    const unsubscribe = onSnapshot(
      doc(firebaseDB, COLLECTIONS.Boxes, boxID),
      (snapshot) => {
        setState({
          boxID,
          box: snapshot.exists()
            ? ({ id: snapshot.id, ...snapshot.data() } as Box & { id: string })
            : null,
          error: false,
        });
      },
      (err) => {
        console.error("Error subscribing to box:", err);
        setState({ boxID, box: null, error: true });
      }
    );

    return unsubscribe;
  }, [boxID, retryToken]);

  const resolved = boxID !== null && state.boxID === boxID;
  return {
    box: resolved ? state.box : null,
    isLoading: boxID !== null && !resolved,
    error: resolved && state.error,
  };
};
